import React, { useState, useEffect, useCallback } from 'react';
import { BasicLayout } from '@/components/BasicLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { usePermissions } from '@/contexts/PermissionsContext';
import { 
  Search, Filter, RefreshCw, Download, Clock, User, 
  FileText, Edit, Trash2, Plus, Eye, Activity 
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Action icons mapping
const ACTION_ICONS = {
  create: { icon: Plus, color: 'text-green-600 bg-green-50' },
  update: { icon: Edit, color: 'text-blue-600 bg-blue-50' },
  delete: { icon: Trash2, color: 'text-red-600 bg-red-50' },
  login: { icon: User, color: 'text-purple-600 bg-purple-50' },
  export: { icon: Download, color: 'text-orange-600 bg-orange-50' },
  import: { icon: FileText, color: 'text-teal-600 bg-teal-50' },
};

// Resource icons mapping
const RESOURCE_ICONS = {
  lead: '🎯',
  booking: '📋',
  master: '⚙️',
  user: '👤',
  company: '🏢',
  template: '📝',
  service_job: '🔧',
  settings: '⚙️',
};

function AuditLogs({ user, onLogout }) {
  const { isCompanyAdmin, isSuperAdmin } = usePermissions();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    resource_type: '',
    user_email: '',
    date_from: '',
    date_to: '',
  });
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 50,
    total: 0,
    hasMore: false,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const fetchLogs = useCallback(async (resetPagination = false) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.action) params.append('action', filters.action);
      if (filters.resource_type) params.append('resource_type', filters.resource_type);
      if (filters.user_email) params.append('user_email', filters.user_email);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const skip = resetPagination ? 0 : pagination.skip;
      params.append('skip', skip.toString());
      params.append('limit', pagination.limit.toString());

      const response = await axios.get(`${API}/api/audit/logs?${params.toString()}`, {
        headers: getAuthHeaders(),
      });

      setLogs(response.data.logs);
      setPagination({
        skip: response.data.skip,
        limit: response.data.limit,
        total: response.data.total,
        hasMore: response.data.has_more,
      });
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.skip, pagination.limit, getAuthHeaders]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/audit/logs/summary?days=30`, {
        headers: getAuthHeaders(),
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchLogs(true);
    fetchSummary();
  }, []);

  useEffect(() => {
    fetchLogs(true);
  }, [filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const clearFilters = () => {
    setFilters({
      action: '',
      resource_type: '',
      user_email: '',
      date_from: '',
      date_to: '',
    });
  };

  const loadMore = () => {
    setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }));
    fetchLogs();
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionIcon = (action) => {
    const config = ACTION_ICONS[action] || { icon: Activity, color: 'text-gray-600 bg-gray-50' };
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${config.color}`}>
        <Icon className="w-4 h-4" />
      </span>
    );
  };

  const getResourceIcon = (resourceType) => {
    return RESOURCE_ICONS[resourceType] || '📄';
  };

  // Check permissions
  if (!isCompanyAdmin && !isSuperAdmin) {
    return (
      <BasicLayout user={user} onLogout={onLogout} activeModule="settings">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-xl font-semibold text-gray-700">Access Denied</h2>
            <p className="text-gray-500">Only Company Admins can view audit logs.</p>
          </div>
        </div>
      </BasicLayout>
    );
  }

  return (
    <BasicLayout user={user} onLogout={onLogout} activeModule="settings">
      <div className="space-y-6" data-testid="audit-logs-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Audit Logs</h1>
            <p className="text-gray-500">Track all changes and activities in the system</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                showFilters ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
              } hover:bg-blue-50`}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
            <button
              onClick={() => { fetchLogs(true); fetchSummary(); }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Activity className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Actions (30d)</p>
                  <p className="text-2xl font-bold text-gray-800">{summary.total_actions}</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-50 rounded-lg">
                  <Plus className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Creates</p>
                  <p className="text-2xl font-bold text-gray-800">{summary.by_action?.create || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Edit className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Updates</p>
                  <p className="text-2xl font-bold text-gray-800">{summary.by_action?.update || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-lg">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Deletes</p>
                  <p className="text-2xl font-bold text-gray-800">{summary.by_action?.delete || 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                <select
                  value={filters.action}
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="filter-action"
                >
                  <option value="">All Actions</option>
                  <option value="create">Create</option>
                  <option value="update">Update</option>
                  <option value="delete">Delete</option>
                  <option value="login">Login</option>
                  <option value="export">Export</option>
                  <option value="import">Import</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resource</label>
                <select
                  value={filters.resource_type}
                  onChange={(e) => handleFilterChange('resource_type', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="filter-resource"
                >
                  <option value="">All Resources</option>
                  <option value="lead">Leads</option>
                  <option value="booking">Bookings</option>
                  <option value="master">Masters</option>
                  <option value="user">Users</option>
                  <option value="template">Templates</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User Email</label>
                <input
                  type="text"
                  value={filters.user_email}
                  onChange={(e) => handleFilterChange('user_email', e.target.value)}
                  placeholder="Search by email"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="filter-user-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="filter-date-from"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="filter-date-to"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                data-testid="clear-filters-btn"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}

        {/* Logs Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Timestamp</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Action</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Resource</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">User</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Details</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">View</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-blue-600" />
                      <p className="text-gray-500 mt-2">Loading audit logs...</p>
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center">
                      <Activity className="w-12 h-12 mx-auto text-gray-300" />
                      <p className="text-gray-500 mt-2">No audit logs found</p>
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50" data-testid={`audit-log-row-${log.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">{formatTimestamp(log.timestamp)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getActionIcon(log.action)}
                          <span className="font-medium capitalize">{log.action}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2">
                          <span>{getResourceIcon(log.resource_type)}</span>
                          <span className="capitalize">{log.resource_type}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-800">{log.user_name || '-'}</p>
                          <p className="text-sm text-gray-500">{log.user_email || '-'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-600 max-w-xs truncate">
                          {log.details?.customer_name && <span>Customer: {log.details.customer_name} | </span>}
                          {log.details?.lead_no && <span>Lead #{log.details.lead_no} | </span>}
                          {log.details?.booking_no && <span>Booking #{log.details.booking_no} | </span>}
                          {log.details?.name && <span>{log.details.name} | </span>}
                          {log.details?.changed_fields && (
                            <span className="text-blue-600">Changed: {log.details.changed_fields.join(', ')}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setSelectedLog(log)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="View Details"
                          data-testid={`view-log-btn-${log.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 bg-gray-50 border-t flex justify-between items-center">
            <span className="text-sm text-gray-600">
              Showing {pagination.skip + 1} - {Math.min(pagination.skip + logs.length, pagination.total)} of {pagination.total}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }));
                  fetchLogs();
                }}
                disabled={pagination.skip === 0}
                className="px-4 py-2 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={loadMore}
                disabled={!pagination.hasMore}
                className="px-4 py-2 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Log Detail Modal */}
        {selectedLog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
                <h2 className="text-xl font-bold">Audit Log Details</h2>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  data-testid="close-log-modal"
                >
                  ✕
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Action</label>
                    <p className="font-semibold capitalize flex items-center gap-2">
                      {getActionIcon(selectedLog.action)}
                      {selectedLog.action}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Resource Type</label>
                    <p className="font-semibold capitalize">
                      {getResourceIcon(selectedLog.resource_type)} {selectedLog.resource_type}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Resource ID</label>
                    <p className="font-mono text-sm bg-gray-100 p-1 rounded">{selectedLog.resource_id || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Timestamp</label>
                    <p>{formatTimestamp(selectedLog.timestamp)}</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <label className="text-sm font-medium text-gray-500">User</label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-lg">
                    <p className="font-semibold">{selectedLog.user_name || 'Unknown'}</p>
                    <p className="text-sm text-gray-600">{selectedLog.user_email || '-'}</p>
                    <p className="text-sm text-blue-600">{selectedLog.user_role || '-'}</p>
                  </div>
                </div>

                {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                  <div className="border-t pt-4">
                    <label className="text-sm font-medium text-gray-500">Details</label>
                    <pre className="mt-1 p-3 bg-gray-50 rounded-lg text-sm overflow-x-auto">
                      {JSON.stringify(selectedLog.details, null, 2)}
                    </pre>
                  </div>
                )}

                {selectedLog.old_values && Object.keys(selectedLog.old_values).length > 0 && (
                  <div className="border-t pt-4">
                    <label className="text-sm font-medium text-gray-500">Previous Values</label>
                    <pre className="mt-1 p-3 bg-red-50 rounded-lg text-sm overflow-x-auto max-h-60">
                      {JSON.stringify(selectedLog.old_values, null, 2)}
                    </pre>
                  </div>
                )}

                {selectedLog.new_values && Object.keys(selectedLog.new_values).length > 0 && (
                  <div className="border-t pt-4">
                    <label className="text-sm font-medium text-gray-500">New Values</label>
                    <pre className="mt-1 p-3 bg-green-50 rounded-lg text-sm overflow-x-auto max-h-60">
                      {JSON.stringify(selectedLog.new_values, null, 2)}
                    </pre>
                  </div>
                )}

                {selectedLog.ip_address && (
                  <div className="border-t pt-4">
                    <label className="text-sm font-medium text-gray-500">IP Address</label>
                    <p className="font-mono text-sm">{selectedLog.ip_address}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </BasicLayout>
  );
}

export default AuditLogs;
